import { FEATURE_FLAGS, RuleTags } from "@/data/globals";
import {
  BalValModel,
  BalValUnit,
  BalanceInvalidReasons,
  GainType,
  ISpecialRule,
  IUnit,
  IUpgradeGains,
  IUpgradeGainsItem,
  IUpgradeGainsRule,
  IUpgradeGainsWeapon,
  IUpgradeOption,
  IUpgradePackage,
  SpellType,
  UpgradeError,
  ValidationError,
  ValidationErrorType,
  WoundLimits,
  WoundPotential,
  noWounds,
  sumWoundPotentials
} from "@/data/types";
import { isFeatureEnabled } from "@/env";
import { every, groupBy, sumBy } from "lodash-es";
import CalcService from "../calc/calcService";
import { flagEnabled, getUnitRules, getUnitWeapons, isItem, isWeapon } from "../Helpers";
import UnitService from "../UnitService";

const ENABLE_V360 = isFeatureEnabled(FEATURE_FLAGS.V3_6);

export default class BalanceValidationService {
  constructor(private _calcService: CalcService) {}

  public validate(
    allUnits: IUnit[],
    allUpgradePackages: IUpgradePackage[],
    spells: { type?: SpellType; threshold: number; spellbookId?: string; generation?: any }[]
  ): { valid: boolean; reason: BalanceInvalidReasons } {
    // Filter out any units from parent books - ignore balval warnings for inherited
    const units = allUnits.filter((x) => !x.sync);
    const upgradePackages = allUpgradePackages.filter((x) => !x.sync);

    let invalidReason = 0;
    if (units.some((x) => x.hasBalanceInvalid)) invalidReason |= BalanceInvalidReasons.Wounds;

    const hasCustomRules = units.some((x) => x.hasCustomRule);
    if (hasCustomRules) invalidReason |= BalanceInvalidReasons.CustomRules;

    const hasCalcErrors = upgradePackages.some((x) =>
      flagEnabled(x.calcErrorReason, UpgradeError.Error | UpgradeError.Prerequisites)
    );
    if (hasCalcErrors) invalidReason |= BalanceInvalidReasons.CalcErrors;

    const hasNegativeCalc = upgradePackages.some((x) =>
      flagEnabled(x.calcErrorReason, UpgradeError.Negative)
    );
    if (hasNegativeCalc) invalidReason |= BalanceInvalidReasons.NegativeUpgrades;

    const hasFreeCalc = upgradePackages.some((x) => flagEnabled(x.calcErrorReason, UpgradeError.Free));
    if (hasFreeCalc) invalidReason |= BalanceInvalidReasons.FreeUpgrades;

    const { valid: hasValidSpells } = BalanceValidationService.getSpellListValid(spells);
    if (!hasValidSpells) invalidReason |= BalanceInvalidReasons.Spells;

    const hasCustomSpells = spells.some((x) => !x.spellbookId && !x.generation);
    if (hasCustomSpells) invalidReason |= BalanceInvalidReasons.CustomSpells;

    const bookIsValid = every(units, (x) => x.valid) && !hasCalcErrors && !hasNegativeCalc && hasValidSpells;

    return { valid: bookIsValid, reason: invalidReason };
  }

  public static getLimitsForUnit(unit: IUnit): WoundLimits {
    const tough = UnitService.getTough(unit);
    const size = unit.size;
    return this.getLimitsBase(tough, size, getUnitRules(unit));
  }

  public static getLimitsForModel(model: BalValModel, unitSize: number, tough: number): WoundLimits {
    return this.getLimitsBase(tough, unitSize, model.specialRules.concat(model.loadout));
  }

  private static getLimitsBase(tough: number, size: number, specialRules: { name: string }[]): WoundLimits {
    const isArtillery = ["Entrenched", "Artillery", "Mobile Artillery"].some((x) =>
      specialRules.some((rule) => rule.name === x)
    );
    const isAircraft = ["Aircraft"].some((x) => specialRules.some((rule) => rule.name === x));
    const isSupport = size === 3 && tough === 1;

    const unitRatio = (() => {
      // "Support" units of [3] models get 400% ratio
      if (isSupport) return 400;

      if (ENABLE_V360) {
        // Units of [1] and Artillery units (which will be [1] anyway?) get 200% ratio
        if (tough === 1 || isArtillery) return 200;
        // Tough(6) and Aircraft units get 150% ratio
        if (tough === 6 || isAircraft) return 150;
      } else {
        // Units of [1], Artillery and Aircraft units get 200% ratio
        if (tough === 1 || isArtillery || isAircraft) return 200;
      }

      // Everyone else gets 133%
      return 133.4;
    })();

    const specialistCount = (() => {
      // Can't have a specialist if it's a single model unit
      if (size === 1) return 0;
      // Units of [3] models are "support" units where everyone is special
      if (isSupport) return 3;
      // Size 5/10 will get 1/2 specialists, not sure what the other one is for...
      return size % 5 === 0 ? size / 5 : size / 3;
    })();

    const specialistRatio = (() => {
      if (ENABLE_V360) {
        if (tough > 1) return 267;
        if (isSupport) return 800;
      } else {
        if (tough > 1) return 200;
      }

      return 400;
    })();

    return {
      unitRatio,
      specialistCount,
      specialistRatio,
      unit: Math.floor((tough * unitRatio) / 100),
      specialist: Math.floor((tough * specialistRatio) / 100)
    };
  }

  public getUnitSizeValid(unit: IUnit, tough: number) {
    const singleModelRules = [
      "Hero",
      "Unique Hero",
      "Entrenched",
      "Artillery",
      "Mobile Artillery",
      "Aircraft"
    ];
    const isAllowedSingle = getUnitRules(unit).some((x) => singleModelRules.includes(x.name));
    if (isAllowedSingle) return unit.size === 1 && tough >= 3;
    switch (tough) {
      case 1:
        return [3, 5, 10].includes(unit.size);
      case 3:
        return unit.size === 3;
    }
    return unit.size === 1;
  }

  public getUnitMeleeValid(unit: IUnit, tough: number) {
    const unitRules = getUnitRules(unit);
    const exempt = unitRules.some((x) => ["Aircraft", "Immobile", "Artillery"].includes(x.name));
    if (exempt) return true;

    const unitWeapons = getUnitWeapons(unit);
    const hasMeleeWeapon = unitWeapons.some((e: any) => e.range == 0 || !e.range);
    if (hasMeleeWeapon) return true;

    if (ENABLE_V360) {
      const hasAssaultWeapon = unitWeapons.some((e) => e.specialRules.some((r) => r.name === "Assault"));
      if (hasAssaultWeapon) return true;
    }

    // T6+ can have their melee satisfied by special rules
    // OR it's v3.6 where this can apply to any unit
    if (ENABLE_V360 || tough >= 6) {
      for (const rule of unitRules) {
        const ruleDef = this._calcService.dataRules.find((x) => x.id === rule.id);
        const isMeleeBalvalRule = ruleDef?.tags?.includes(RuleTags.MeleeBalval);
        if (!isMeleeBalvalRule) continue;

        // in v3.6 just having a melee rule is enough, we don't care about the rating
        if (ENABLE_V360) return true;

        // in <= v3.5 the melee rule needs a rating of 3+ to satisfy the melee requirement
        const ruleRating = parseInt(rule.rating as any);
        if (ruleRating >= 3) return true;
      }
    }

    // Unit doesn't have any melee, assault, or qualifying rules
    return false;
  }

  public specialRuleLimits = {
    Hero: 1,
    Caster: 1
    // Aircraft: 1,
    // Entrenched: 1,
    // Transport: 1
    //Fear: 1, // No longer necessary
  };

  public getUnitSpecialRulesValid(unitSize: number, specialRules: ISpecialRule[]): ValidationError[] {
    const errors: ValidationError[] = [];

    const maximums = {
      Tough: 30,
      Transport: 21,
      Caster: 4
    };

    for (const rule of specialRules) {
      const limit = this.specialRuleLimits[rule.name];
      if (limit && unitSize > limit) {
        errors.push({
          type: ValidationErrorType.rules,
          message: `Invalid special rules: Max. ${limit} model with ${rule.name}`
        });
      }

      const max = maximums[rule.name];
      if (max && parseInt(rule.rating as any) > max) {
        errors.push({
          type: ValidationErrorType.rules,
          message: `Invalid special rules: ${rule.name} has a maximum allowed rating of ${max}`
        });
      }
    }

    return errors;
  }

  public getUnitCustomRules(unit: IUnit) {
    const customRules = this._calcService.dataRules.filter((x) => x.custom);
    return getUnitRules(unit).filter((x) => customRules.some((cr) => cr.name === x.name));
  }

  public isCustomRule(rule: string) {
    const customRules = this._calcService.dataRules.filter((x) => x.custom && !x.aliasedRuleId);
    return customRules.some((cr) => cr.name === rule);
  }

  public static getSpellListValid(spells: { type?: SpellType; threshold: number }[]): {
    valid: boolean;
    message?: string;
  } {
    // If there aren't any spells, that's fine
    if (spells.length === 0) {
      return { valid: true };
    }

    // Must be 6 spells
    if (spells.length !== 6) {
      return {
        valid: false,
        message: "Must have 6 spells. (Optional: for lists without Caster, may have 0 spells)"
      };
    }

    // Group spells by threshold <-- who does comments like this?!
    const byThreshold = groupBy(spells, "threshold");

    for (const key of Object.keys(byThreshold)) {
      const group = byThreshold[key];

      // Must be 2 spells of each tier
      if (group.length !== 2)
        return { valid: false, message: "Must have two spells at each difficulty level." };

      // If one spell is attack, the other must be buff/debuff
      const hasAttackSpell = group.filter((x) => x.type === SpellType.Attack).length === 1;

      // Must have one buff and one attack spell
      if (!hasAttackSpell) return { valid: false, message: "Must have 1 attack spell per difficulty." };
    }

    const buffCount = spells.filter((x) => x.type === SpellType.Buff).length;
    if (buffCount > 2) return { valid: false, message: "Must not have more than 2 buff spells." };

    const debuffCount = spells.filter((x) => x.type === SpellType.Debuff).length;
    if (debuffCount > 2) return { valid: false, message: "Must not have more than 2 debuff spells." };

    return { valid: true };
  }

  public getUnitBaseValid(originalUnit: IUnit, tough: number, limits: WoundLimits) {
    const originalUnitRules = getUnitRules(originalUnit);
    const hero = originalUnitRules.some((x) => x.name === "Hero");
    const unit = hero
      ? {
          ...originalUnit,
          specialRules: originalUnitRules.filter((x) => !(x.name === "Impact" && x.rating == 1))
        }
      : originalUnit;

    const unitWoundPotential = this.getUnitWoundPotential(unit);
    const woundsValid =
      (unitWoundPotential.melee / tough) * 100 <= limits.unitRatio &&
      (unitWoundPotential.ranged / tough) * 100 <= limits.unitRatio;
    const hasCustomRules = this.getUnitCustomRules(unit).length > 0;
    const sizeValid = this.getUnitSizeValid(unit, tough);
    const meleeValid = this.getUnitMeleeValid(unit, tough);
    return {
      valid: woundsValid && !hasCustomRules && sizeValid && meleeValid,
      woundsValid,
      customRulesValid: !hasCustomRules,
      sizeValid,
      meleeValid
    };
  }

  public getUnitWoundPotential(unit: IUnit) {
    const loadout = (unit as any).loadout || getUnitWeapons(unit);

    // Get wounds from unit rules
    const normalised = CalcService.normalizeUnit(unit);
    const woundPotentials = unit.rules.map((rule) =>
      this.getUnitRuleWoundPotential(rule.name, rule.rating, normalised)
    );
    const unitWounds = sumWoundPotentials(woundPotentials);
    const unitTough = UnitService.getTough(unit);

    // Wounds from weapons
    const loadoutWounds = loadout.map((x) => ({
      name: x.name,
      wounds: isItem(x)
        ? sumWoundPotentials(
            x.content
              .filter((c) => isWeapon(c))
              .map((c) => this.getWeaponWoundPotential(c as IUpgradeGainsWeapon, unitTough))
          )
        : this.getWeaponWoundPotential(x as IUpgradeGainsWeapon, unitTough)
    }));

    return sumWoundPotentials([unitWounds, ...loadoutWounds.map((x) => x.wounds)]);
  }

  public getModelWoundPotential(model: BalValModel, tough: number) {
    // Wounds from weapons
    const loadoutWounds = model.loadout.map((x) => {
      if (x.woundPotential) {
        return x.woundPotential;
      }

      switch (x.type) {
        case GainType.Item:
          return this.getItemWoundPotential(x, tough);
        case GainType.Weapon:
          return this.getWeaponWoundPotential(x as IUpgradeGainsWeapon, tough);
        case GainType.Rule:
          return this.getUnitRuleWoundPotential(x.name, x.rating, model);
      }
      return { melee: 0, ranged: 0 };
    });

    return sumWoundPotentials([...loadoutWounds, model.ruleWounds]);
  }

  public getUnitRuleWoundPotential(name: string, rating: any, unit: any) {
    const rule = this._calcService.unitRules.find((rule) => rule.name === name);
    return {
      melee: typeof rule?.woundFormula === "function" ? parseInt(rule.woundFormula(rating, unit)) : 0,
      ranged:
        typeof rule?.rangedWoundFormula === "function" ? parseInt(rule.rangedWoundFormula(rating, unit)) : 0
    };
  }

  public getWeaponWoundPotential(weapon: IUpgradeGainsWeapon, unitTough: number) {
    // TODO Make Limited scale with Tough: -3 until T9, then -6 until T21, and then -9 from T24+
    const limitedValue = (() => {
      const isLimitedWeapon = weapon.specialRules.some((x) => x.name === "Limited");
      if (!isLimitedWeapon) return 0;
      if (unitTough <= 9) return 3;
      if (unitTough <= 21) return 6;
      return 9;
    })();

    const isAssaultWeapon = weapon.specialRules.some((x) => x.name === "Assault");
    const isRanged = weapon.range > 0;
    const isMelee = !isRanged;

    const rulesMultiplier =
      sumBy(weapon.specialRules, (x) => {
        const rule = this._calcService.weaponRules.find((rule) => rule.name === x.name);
        return typeof rule?.woundFormula === "function"
          ? parseInt(rule.woundFormula((x as IUpgradeGainsRule).rating))
          : 0;
      }) || 1;
    const maxWounds = Math.max(0, (weapon.count ?? 1) * weapon.attacks * rulesMultiplier - limitedValue);
    return {
      melee: isMelee || isAssaultWeapon ? maxWounds : 0,
      ranged: isRanged || isAssaultWeapon ? maxWounds : 0
    } as WoundPotential;
  }

  public getOptionWoundPotential(option: IUpgradeOption, tough: number): WoundPotential {
    return sumWoundPotentials(option.gains.map((x) => this.getOptionGainWoundPotential(x, tough)));
  }

  public getOptionGainWoundPotential(x: IUpgradeGains, tough: number) {
    try {
      switch (x.type) {
        case GainType.Weapon:
          const weapon = x as IUpgradeGainsWeapon;
          return this.getWeaponWoundPotential(weapon, tough);
        case GainType.Item:
          return this.getItemWoundPotential(x as IUpgradeGainsItem, tough);
        case GainType.Rule:
          return this.getUnitRuleWoundPotential(x.name, (x as IUpgradeGainsRule).rating, null);
      }
      return noWounds;
    } catch (e) {
      console.log(e, x);
    }
  }

  public getOptionTough(option: IUpgradeOption): number {
    return (
      sumBy(
        option.gains.filter((x) => x.type === GainType.Item),
        (item) =>
          parseInt(
            ((item as IUpgradeGainsItem).content.find((x) => x.name === "Tough") as IUpgradeGainsRule)
              ?.rating || "0"
          )
      ) || 0
    );
  }

  public getItemWoundPotential(item: IUpgradeGainsItem, tough?: number): WoundPotential {
    return sumWoundPotentials(
      item.content.map((x) => {
        switch (x.type) {
          case GainType.Weapon:
            const weapon = x as IUpgradeGainsWeapon;
            return this.getWeaponWoundPotential(weapon, tough);
          case GainType.Rule:
            return this.getUnitRuleWoundPotential(x.name, (x as IUpgradeGainsRule).rating, null);
        }
        return noWounds;
      })
    );
  }

  public static getModelTough(unit: BalValUnit, model: BalValModel): number {
    const itemContent = model.loadout.filter((x) => x.type === GainType.Item).flatMap((x) => x.content);

    const toughUpgrade =
      sumBy(
        model.loadout.concat(itemContent).filter((x) => x.name === "Tough"),
        (x) => parseInt(x.rating)
      ) ?? 0;

    return toughUpgrade + unit.baseTough;
  }
}
